<template>
  <modal name="mappingRuleModal" height="auto" width="94%" :scrollable="true" :clickToClose="false">
    <CloseModal :close="close" />
    <div class="modalbox">
      <div class="modalbox-content">

        <!-- Breadcrumb: Consulta → campo destino -->
        <div class="d-flex align-items-center mb-2">
          <span v-if="form.transformer_key" class="text-muted small mr-2">
            <fa-icon icon="cloud-download-alt" /> {{ getTransformerName(form.transformer_key) }}
          </span>
          <fa-icon v-if="form.transformer_key && form.verso_field" icon="arrow-right" class="text-muted mr-2 small" />
          <CBadge v-if="form.verso_field" color="primary" class="px-2">{{ form.verso_field }}</CBadge>
          <span v-else class="text-muted small font-italic">campo destino não selecionado</span>
        </div>

        <!-- Selector de transformer se não fixado -->
        <div v-if="!isEditing && !form.transformer_key" class="form-group">
          <label class="small font-weight-bold text-muted text-uppercase">Consulta</label>
          <select class="form-control form-control-sm" v-model="form.transformer_key" @change="onTransformerChange">
            <option value="">Selecione...</option>
            <option v-for="t in transformers" :key="t.transformer_key" :value="t.transformer_key">{{ t.display_name }}</option>
          </select>
        </div>

        <!-- Tabs de tipo de regra -->
        <div v-if="form.transformer_key && !loadingPreview" class="mb-3">
          <small class="font-weight-bold text-muted text-uppercase d-block mb-1" style="font-size:10px;letter-spacing:0.4px">Tipo de mapeamento</small>
          <div class="cd-type-tabs">
          <CButton
            v-for="key in ruleOrder.filter(k => transformTypes[k])"
            :key="key"
            size="sm"
            :color="form.transform_type === key ? 'primary' : 'light'"
            class="mr-1 mb-1"
            @click="setTransformType(key)"
            :title="typeExamples[key]"
          >{{ transformTypes[key].label }}</CButton>
          </div>
        </div>

        <div v-if="loadingPreview" class="text-center py-4 text-muted">
          <fa-icon icon="spinner" spin /> Carregando campos da API...
        </div>

        <!-- Corpo: árvore ERP + formulário -->
        <div v-if="form.transformer_key && !loadingPreview" class="row no-gutters cd-modal-body">

          <!-- Árvore ERP (oculta para tipos sem campo de origem) -->
          <div v-if="needsErpTree" class="col-12 col-md-5 cd-tree-col border-right">
            <div class="p-2 bg-light border-bottom d-flex align-items-center">
              <fa-icon icon="cloud-download-alt" class="mr-1 text-muted" />
              <small class="font-weight-bold text-muted text-uppercase">Campos da API (ERP)</small>
            </div>

            <!-- Hint de seleção -->
            <div class="px-2 py-1 border-bottom" :class="isMultiSourceMode ? 'bg-info-light' : (form.source_expression ? 'bg-success-light' : 'bg-light')">
              <small :class="isMultiSourceMode ? 'text-info' : (form.source_expression ? 'text-success font-weight-bold font-monospace' : 'text-muted')">
                <fa-icon :icon="isMultiSourceMode ? 'list-ol' : (form.source_expression ? 'check-circle' : 'hand-pointer')" />
                <span v-if="isMultiSourceMode && sourcesList.length"> {{ sourcesList.length }} campo(s) — clique para adicionar mais</span>
                <span v-else-if="isMultiSourceMode"> Clique nos campos para definir a ordem</span>
                <span v-else-if="form.source_expression"> {{ form.source_expression }}</span>
                <span v-else> Clique no campo de origem</span>
              </small>
            </div>

            <!-- Sugestões rápidas (direct) -->
            <div v-if="form.transform_type === 'direct' && quickSuggestions.length" class="px-2 pt-1 pb-1 border-bottom bg-light">
              <small class="text-muted text-uppercase font-weight-bold d-block mb-1" style="font-size:10px;letter-spacing:0.3px">Sugeridos para <code>{{ form.verso_field }}</code></small>
              <CButton
                v-for="s in quickSuggestions"
                :key="s"
                size="sm"
                :color="form.source_expression === s ? 'primary' : 'info'"
                variant="outline"
                class="mr-1 mb-1 font-monospace"
                style="font-size:11px"
                @click="onFieldSelect(s)"
              >{{ s }}</CButton>
            </div>

            <div class="px-2 pt-1">
              <input type="text" class="form-control form-control-sm" placeholder="Buscar campo..." v-model="treeSearch" />
            </div>
            <div class="cd-tree-scroll">
              <template v-if="Object.keys(filteredPreview).length > 0">
                <mrm-node
                  v-for="(value, key) in filteredPreview"
                  :key="String(key)"
                  :fieldKey="String(key)"
                  :value="value"
                  :path="String(key)"
                  :depth="0"
                  :selectedPath="isMultiSourceMode ? '' : form.source_expression"
                  :mappedPaths="isMultiSourceMode ? multiSourceMap : {}"
                  @select="isMultiSourceMode ? onFieldAdd($event) : onFieldSelect($event)"
                />
              </template>
              <div v-else class="text-center text-muted small py-4">Nenhum campo disponível</div>
            </div>
          </div>

          <!-- Formulário de configuração -->
          <div :class="['cd-form-col', needsErpTree ? 'col-12 col-md-7' : 'col-12']">
            <div class="p-3">

              <!-- Campo destino se não fixado -->
              <div v-if="!form.verso_field" class="form-group">
                <label class="small font-weight-bold text-muted text-uppercase">Campo destino (Verso)</label>
                <verso-combobox :value="form.verso_field" :options="versoColumns" @input="form.verso_field = $event" />
              </div>

              <!-- Descrição do tipo -->
              <div v-if="currentTypeInfo" class="alert alert-light py-2 px-3 mb-3 small">
                <strong>{{ currentTypeInfo.description }}</strong>
                <div class="text-muted mt-1"><fa-icon icon="lightbulb" class="text-warning" /> {{ typeExamples[form.transform_type] }}</div>
              </div>

              <!-- Config: FIXED -->
              <div v-if="form.transform_type === 'fixed'" class="form-group">
                <label class="small font-weight-bold text-muted text-uppercase">Valor fixo</label>
                <input type="text" class="form-control form-control-sm" v-model="configProxy.value" placeholder="Ex: 1, ativo, BRL" />
              </div>

              <!-- Config: FORMAT -->
              <div v-if="form.transform_type === 'format'" class="form-group">
                <label class="small font-weight-bold text-muted text-uppercase">Formato</label>
                <select class="form-control form-control-sm" v-model="configProxy.format">
                  <option value="">Selecione…</option>
                  <option value="mask:cep">CEP (00000-000)</option>
                  <option value="mask:cpf_cnpj">CPF/CNPJ</option>
                  <option value="number:2">Decimal (2 casas)</option>
                  <option value="uppercase">Maiúsculas</option>
                  <option value="lowercase">Minúsculas</option>
                  <option value="trim">Remover espaços</option>
                </select>
              </div>

              <!-- Config: MAP -->
              <div v-if="form.transform_type === 'map'" class="form-group">
                <div class="d-flex align-items-center justify-content-between mb-1">
                  <label class="small font-weight-bold text-muted text-uppercase mb-0">Tabela DE → PARA</label>
                  <small v-if="form.source_expression" class="text-muted">campo: <code>{{ form.source_expression }}</code></small>
                </div>
                <div :class="mapRows.length > 5 ? 'row no-gutters' : ''">
                  <div
                    v-for="(row, idx) in mapRows"
                    :key="idx"
                    :class="mapRows.length > 5 ? 'col-12 col-md-6' : ''"
                    class="mb-1"
                  >
                    <div class="input-group input-group-sm">
                      <input type="text" class="form-control form-control-sm font-monospace" v-model="row.from" placeholder="Valor API" />
                      <div class="input-group-append input-group-prepend"><span class="input-group-text">→</span></div>
                      <input type="text" class="form-control form-control-sm font-monospace" v-model="row.to" placeholder="Valor Verso" />
                      <div class="input-group-append">
                        <CButton size="sm" color="danger" variant="outline" @click="mapRows.splice(idx, 1)" title="Remover">
                          <fa-icon icon="times" />
                        </CButton>
                      </div>
                    </div>
                  </div>
                </div>
                <CButton size="sm" color="primary" variant="outline" class="mt-1" @click="mapRows.push({ from: '', to: '' })">
                  <fa-icon icon="plus" /> Adicionar par
                </CButton>
              </div>

              <!-- Config: CONDITIONAL -->
              <div v-if="form.transform_type === 'conditional'" class="form-group">
                <label class="small font-weight-bold text-muted text-uppercase">Condição</label>
                <div class="row">
                  <div class="col-12 col-md-3 mb-2">
                    <small class="text-muted d-block">Operador</small>
                    <select class="form-control form-control-sm" v-model="configProxy.operator">
                      <option value="equals">Igual a</option>
                      <option value="not_equals">Diferente de</option>
                      <option value="contains">Contém</option>
                      <option value="is_empty">Está vazio</option>
                      <option value="is_not_empty">Não está vazio</option>
                      <option value="gt">Maior que</option>
                      <option value="lt">Menor que</option>
                    </select>
                  </div>
                  <div class="col-12 col-md-3 mb-2">
                    <small class="text-muted d-block">Comparar com</small>
                    <input type="text" class="form-control form-control-sm" v-model="configProxy.value" placeholder="Ex: cLid" />
                  </div>
                  <div class="col-12 col-md-3 mb-2">
                    <small class="text-muted d-block">Se verdadeiro</small>
                    <input type="text" class="form-control form-control-sm" v-model="configProxy.then" placeholder="Ex: prospect" />
                  </div>
                  <div class="col-12 col-md-3 mb-2">
                    <small class="text-muted d-block">Se falso</small>
                    <input type="text" class="form-control form-control-sm" v-model="configProxy.else" placeholder="Ex: cliente" />
                  </div>
                </div>
              </div>

              <!-- Config: FIRST_MATCH -->
              <div v-if="form.transform_type === 'first_match'" class="form-group">
                <!-- Modo do conjunto -->
                <label class="small font-weight-bold text-muted text-uppercase d-block">Conjunto de campos</label>
                <div class="btn-group btn-group-sm mb-2" role="group">
                  <CButton
                    size="sm"
                    :color="configProxy.set_mode !== 'list' ? 'primary' : 'light'"
                    @click="$set(configProxy, 'set_mode', 'pattern')"
                  >Por padrão</CButton>
                  <CButton
                    size="sm"
                    :color="configProxy.set_mode === 'list' ? 'primary' : 'light'"
                    @click="$set(configProxy, 'set_mode', 'list')"
                  >Lista manual</CButton>
                </div>

                <!-- Modo PATTERN -->
                <div v-if="configProxy.set_mode !== 'list'" class="row">
                  <div class="col-12 col-md-6 mb-2">
                    <small class="text-muted d-block">Padrão do campo</small>
                    <input type="text" class="form-control form-control-sm font-monospace" v-model="configProxy.pattern" placeholder="Properties{n}" />
                  </div>
                  <div class="col-6 col-md-3 mb-2">
                    <small class="text-muted d-block">De (mín.)</small>
                    <input type="number" class="form-control form-control-sm" v-model.number="configProxy.range_min" />
                  </div>
                  <div class="col-6 col-md-3 mb-2">
                    <small class="text-muted d-block">Até (máx.)</small>
                    <input type="number" class="form-control form-control-sm" v-model.number="configProxy.range_max" />
                  </div>
                </div>

                <!-- Modo LIST -->
                <div v-else class="mb-2">
                  <small class="text-muted d-block mb-1">Campos a varrer (em ordem)</small>
                  <div v-for="(f, idx) in firstMatchFields" :key="idx" class="input-group input-group-sm mb-1">
                    <input type="text" class="form-control font-monospace" v-model="firstMatchFields[idx]" placeholder="Ex: Properties1" />
                    <div class="input-group-append">
                      <CButton size="sm" color="danger" variant="outline" @click="firstMatchFields.splice(idx, 1)" title="Remover">
                        <fa-icon icon="times" />
                      </CButton>
                    </div>
                  </div>
                  <CButton size="sm" color="primary" variant="outline" @click="firstMatchFields.push('')">
                    <fa-icon icon="plus" /> Adicionar campo
                  </CButton>
                </div>

                <!-- Condição -->
                <div class="row">
                  <div class="col-12 col-md-4 mb-2">
                    <small class="text-muted d-block">Operador</small>
                    <select class="form-control form-control-sm" v-model="configProxy.operator">
                      <option value="equals">Igual a</option>
                      <option value="not_equals">Diferente de</option>
                      <option value="contains">Contém</option>
                      <option value="is_empty">Está vazio</option>
                      <option value="is_not_empty">Não está vazio</option>
                      <option value="gt">Maior que</option>
                      <option value="lt">Menor que</option>
                    </select>
                  </div>
                  <div v-if="!['is_empty','is_not_empty'].includes(configProxy.operator)" class="col-12 col-md-4 mb-2">
                    <small class="text-muted d-block">Comparar com</small>
                    <input type="text" class="form-control form-control-sm" v-model="configProxy.value" placeholder="Ex: tYES" />
                  </div>
                  <div class="col-12 col-md-4 mb-2">
                    <small class="text-muted d-block">Retornar</small>
                    <select class="form-control form-control-sm" v-model="configProxy.return_mode">
                      <option value="suffix">Sufixo</option>
                      <option value="index">Índice</option>
                      <option value="value">Valor</option>
                      <option value="field_name">Nome do campo</option>
                    </select>
                  </div>
                </div>
              </div>

              <!-- Config: FALLBACK / COALESCE / CONCAT -->
              <div v-if="isMultiSourceMode" class="form-group">
                <label class="small font-weight-bold text-muted text-uppercase">
                  {{ form.transform_type === 'concat' ? 'Campos a concatenar' : 'Ordem de prioridade' }}
                </label>
                <div v-if="sourcesList.length === 0" class="text-muted small py-2">
                  <fa-icon icon="hand-pointer" /> Clique nos campos à esquerda para adicioná-los
                </div>
                <draggable
                  v-else
                  v-model="sourcesList"
                  handle=".drag-handle"
                  ghost-class="cd-drag-ghost"
                  class="mb-2"
                  tag="div"
                >
                  <div v-for="(src, idx) in sourcesList" :key="src + idx" class="d-flex align-items-center mb-1 cd-source-item">
                    <fa-icon icon="grip-vertical" class="drag-handle text-muted mr-2 flex-shrink-0" style="cursor:grab" title="Arrastar para reordenar" />
                    <CBadge color="light" class="text-muted mr-2 flex-shrink-0">#{{ idx + 1 }}</CBadge>
                    <code class="flex-grow-1 text-primary">{{ src }}</code>
                    <CButton size="sm" color="danger" variant="ghost" @click="sourcesList.splice(idx, 1)" :title="'Remover ' + src">
                      <fa-icon icon="times" />
                    </CButton>
                  </div>
                </draggable>
                <div v-if="form.transform_type === 'concat'" class="mt-1">
                  <small class="text-muted d-block">Separador</small>
                  <input type="text" class="form-control form-control-sm" v-model="configProxy.separator" placeholder='Ex: " " ou " / "' style="max-width:160px" />
                </div>
                <div v-if="form.transform_type !== 'concat' && sourcesList.filter(Boolean).length > 1" class="cd-fallback-chain mt-2 p-2 rounded border bg-light small">
                  <span v-for="(f, i) in sourcesList.filter(Boolean)" :key="i">
                    <code class="text-primary">{{ f }}</code>
                    <span v-if="i < sourcesList.filter(Boolean).length - 1" class="text-muted font-italic"> → se vazio → </span>
                  </span>
                  <span class="text-muted font-italic"> → null</span>
                </div>
              </div>

              <!-- Config: CUSTOM -->
              <div v-if="form.transform_type === 'custom'" class="form-group">
                <label class="small font-weight-bold text-muted text-uppercase">Função</label>
                <select class="form-control form-control-sm mb-2" v-model="configProxy.handler">
                  <option value="formatPhones">Formatar telefones</option>
                  <option value="formatCep">Formatar CEP</option>
                  <option value="formatCpfCnpj">Formatar CPF/CNPJ</option>
                  <option value="extractNumeric">Extrair números</option>
                  <option value="trimWhitespace">Limpar espaços</option>
                </select>
                <label class="small font-weight-bold text-muted text-uppercase">Parâmetros</label>
                <div v-for="(p, idx) in paramsList" :key="idx" class="input-group input-group-sm mb-1">
                  <input type="text" class="form-control" v-model="paramsList[idx]" />
                  <div class="input-group-append">
                    <CButton size="sm" color="danger" variant="outline" @click="paramsList.splice(idx, 1)"><fa-icon icon="times" /></CButton>
                  </div>
                </div>
                <CButton size="sm" color="primary" variant="outline" @click="paramsList.push('')"><fa-icon icon="plus" /></CButton>
              </div>

              <!-- Opções extras -->
              <hr class="my-2" />
              <div class="row">
                <div class="col-12 col-md-6 mb-2">
                  <label class="small font-weight-bold text-muted text-uppercase">Valor padrão (se vazio)</label>
                  <input type="text" class="form-control form-control-sm" v-model="form.default_value" placeholder="Ex: 0, null" />
                </div>
                <div class="col-6 col-md-3 mb-2">
                  <label class="small font-weight-bold text-muted text-uppercase">Ativo</label>
                  <select class="form-control form-control-sm" v-model="form.is_active">
                    <option :value="true">Sim</option>
                    <option :value="false">Não</option>
                  </select>
                </div>
                <div class="col-6 col-md-3 mb-2">
                  <label class="small font-weight-bold text-muted text-uppercase">Ordem</label>
                  <input type="number" class="form-control form-control-sm" v-model.number="form.sort_order" />
                </div>
              </div>
              <div class="form-group mb-0">
                <label class="small font-weight-bold text-muted text-uppercase">Observações</label>
                <input type="text" class="form-control form-control-sm" v-model="form.notes" placeholder="Notas internas" />
              </div>
            </div>
          </div>
        </div>

        <!-- Preview sticky -->
        <div v-if="form.transformer_key && !loadingPreview" class="cd-preview-bar mt-2 px-3 py-2 border rounded bg-light d-flex align-items-center flex-wrap" style="gap:8px">
          <small class="font-weight-bold text-muted text-uppercase" style="font-size:10px;letter-spacing:0.3px">Preview</small>
          <code class="text-primary">{{ previewSourceLabel }}</code>
          <fa-icon icon="arrow-right" class="text-muted small" />
          <span :class="transformedValue !== null ? 'text-success font-weight-bold' : 'text-muted'" class="font-monospace small">
            {{ transformedValue !== null ? JSON.stringify(transformedValue) : '—' }}
          </span>
          <fa-icon icon="arrow-right" class="text-muted small" />
          <code class="text-success">{{ form.verso_field || '—' }}</code>
        </div>

      </div><!-- /.modalbox-content -->

      <div class="modal-footer-bar d-flex justify-content-between align-items-center flex-wrap px-3 py-2 border-top bg-light" style="gap:8px">
        <small class="text-muted"><fa-icon icon="info-circle" /> Os vínculos passam a valer na próxima carga de dados.</small>
        <div class="d-flex" style="gap:8px">
          <CButton size="sm" color="secondary" @click="close">Cancelar</CButton>
          <CButton size="sm" color="primary" @click="save" :disabled="!canSave || saving">
            <fa-icon v-if="saving" icon="spinner" spin class="mr-1" />
            <fa-icon v-else icon="check" class="mr-1" />
            {{ saving ? 'Salvando...' : 'Salvar vínculo' }}
          </CButton>
        </div>
      </div>
    </div><!-- /.modalbox -->
  </modal>
</template>

<script>
import { get, post, put } from "../../../helpers/apiRequest";
import CloseModal from "../../../components/CloseModal";
import MrmNode from "./MrmNode";
import VersoCombobox from "./VersoCombobox";
import draggable from "vuedraggable";

export default {
  name: "MappingRuleModal",
  components: { CloseModal, MrmNode, VersoCombobox, draggable },
  props: {
    transformers: { type: Array, default: () => [] },
    transformTypes: { type: Object, default: () => ({}) },
    versoColumns: { type: Array, default: () => [] },
  },
  data() {
    return {
      isEditing: false,
      editId: null,
      saving: false,
      loadingPreview: false,
      previewData: {},
      treeSearch: "",
      form: this.emptyForm(),
      configProxy: {},
      mapRows: [],
      sourcesList: [],
      paramsList: [],
      ruleOrder: ["direct", "map", "fixed", "format", "conditional", "first_match", "fallback", "coalesce", "concat", "system_var", "custom"],
      typeExamples: {
        direct:      "Ex: SalesPersonCode → representante_id. Copia o valor sem alteração.",
        fixed:       "Ex: ignora a API e grava sempre 'BRL'.",
        map:         "Ex: cCustomer → J, cLid → F.",
        conditional: "Ex: se CardType = 'cLid' → tipo = 'prospect', caso contrário → 'cliente'.",
        first_match: "Varre Properties{n} (ou lista) e retorna o 1º que satisfaz a condição.",
        fallback:    "Ex: tenta Email1; se vazio, usa U_EMAIL_ALT.",
        coalesce:    "Ex: tenta Email1; se vazio, usa U_EMAIL_ALT.",
        concat:      "Ex: Street + ', ' + BuildingFloorRoom.",
        format:      "Ex: 12345678000190 → 12.345.678/0001-90.",
        custom:      "Ex: chamar transformerCustomerName($value).",
        system_var:  "Ex: tenant_id, current_user_id, now().",
      },
      firstMatchFields: [],
    };
  },
  computed: {
    canSave() {
      if (!this.form.transformer_key || !this.form.verso_field) return false;
      const t = this.form.transform_type;
      if (t === "direct") return !!this.form.source_expression;
      if (t === "map") return this.mapRows.some(r => r.from);
      if (t === "fallback" || t === "coalesce") return this.sourcesList.filter(Boolean).length >= 1;
      if (t === "concat") return this.sourcesList.filter(Boolean).length >= 1;
      if (t === "fixed") return this.configProxy.value != null && this.configProxy.value !== "";
      if (t === "first_match") {
        return this.configProxy.set_mode === "list"
          ? this.firstMatchFields.filter(Boolean).length >= 1
          : !!(this.configProxy.pattern && this.configProxy.range_max);
      }
      return true;
    },
    currentTypeInfo() { return this.transformTypes[this.form.transform_type] || null; },
    isMultiSourceMode() {
      return ["fallback", "coalesce", "concat"].includes(this.form.transform_type);
    },
    needsErpTree() {
      if (["fixed", "system_var", "custom"].includes(this.form.transform_type)) return false;
      // first_match em modo padrão gera os campos por pattern → não precisa da árvore.
      // Em modo lista, a árvore pode ajudar a descobrir nomes de campos.
      if (this.form.transform_type === "first_match") return this.configProxy.set_mode === "list";
      return true;
    },
    multiSourceMap() {
      return this.sourcesList.reduce((o, f) => { if (f) o[f] = true; return o; }, {});
    },
    filteredPreview() {
      if (!this.treeSearch) return this.previewData;
      const s = this.treeSearch.toLowerCase();
      const r = {};
      for (const [k, v] of Object.entries(this.previewData)) {
        if (k.toLowerCase().includes(s)) r[k] = v;
      }
      return r;
    },
    erpFieldOptions() {
      const flat = [];
      const walk = (obj, prefix) => {
        if (!obj || typeof obj !== "object") return;
        for (const [k, v] of Object.entries(obj)) {
          if (k.startsWith("@")) continue;
          const path = prefix ? `${prefix}.${k}` : k;
          if (Array.isArray(v)) {
            flat.push(path);
            if (v.length > 0 && v[0] && typeof v[0] === "object") walk(v[0], path + ".0");
          } else if (v && typeof v === "object") {
            walk(v, path);
          } else {
            flat.push(path);
          }
        }
      };
      walk(this.previewData, "");
      return flat.sort();
    },
    quickSuggestions() {
      if (!this.form.verso_field || !this.previewData) return [];
      const dest = this.form.verso_field.toLowerCase().replace(/_/g, "");
      return this.erpFieldOptions.filter(path => {
        const key = path.split(".").pop().toLowerCase().replace(/_/g, "");
        return key.includes(dest) || dest.includes(key);
      }).slice(0, 5);
    },
    sourceValue() {
      if (!this.form.source_expression || !this.previewData) return null;
      const parts = this.form.source_expression.split(".");
      let v = this.previewData;
      for (const p of parts) {
        if (v == null || typeof v !== "object") return null;
        v = v[p];
      }
      return v !== undefined ? v : null;
    },
    previewSourceLabel() {
      const t = this.form.transform_type;
      if (this.isMultiSourceMode && this.sourcesList.length) {
        return this.sourcesList.filter(Boolean).join(" → ");
      }
      if (t === "first_match") {
        if (this.configProxy.set_mode === "list") {
          const f = this.firstMatchFields.filter(Boolean);
          return f.length ? f.join(", ") : "—";
        }
        return `${this.configProxy.pattern || "Properties{n}"} [${this.configProxy.range_min}..${this.configProxy.range_max}]`;
      }
      return this.form.source_expression || "—";
    },
    transformedValue() {
      const t = this.form.transform_type;
      if (t === "fixed") return this.configProxy.value != null ? this.configProxy.value : null;
      if (t === "system_var") return "${" + (this.configProxy.var || "var") + "}";

      // Multi-source: fallback/coalesce
      if (t === "fallback" || t === "coalesce") {
        for (const path of this.sourcesList.filter(Boolean)) {
          const parts = path.split(".");
          let v = this.previewData;
          for (const p of parts) { if (v == null) break; v = v[p]; }
          if (v !== null && v !== undefined && v !== "") return v;
        }
        return null;
      }

      // Concat
      if (t === "concat") {
        const sep = this.configProxy.separator || " ";
        const parts = this.sourcesList.filter(Boolean).map(path => {
          const ps = path.split(".");
          let v = this.previewData;
          for (const p of ps) { if (v == null) break; v = v[p]; }
          return v != null && v !== "" ? String(v) : null;
        }).filter(Boolean);
        return parts.length ? parts.join(sep) : null;
      }

      // First match: varre o conjunto de campos e retorna o 1º que satisfaz a condição
      if (t === "first_match") {
        const fields = this.firstMatchFieldList();
        const op = this.configProxy.operator || "equals";
        const cmp = this.configProxy.value;
        const returnMode = this.configProxy.return_mode || "suffix";
        for (let i = 0; i < fields.length; i++) {
          const name = fields[i];
          const parts = name.split(".");
          let v = this.previewData;
          for (const p of parts) { if (v == null || typeof v !== "object") { v = undefined; break; } v = v[p]; }
          if (v === undefined) v = null;
          if (this.evalOperator(op, v, cmp)) {
            if (returnMode === "index") return i + 1;
            if (returnMode === "value") return v;
            if (returnMode === "field_name") return name;
            // suffix: dígitos finais do nome do campo
            const m = String(name).match(/(\d+)$/);
            return m ? m[1] : null;
          }
        }
        return null;
      }

      const src = this.sourceValue;
      if (src === null) return null;
      if (t === "direct") return src;
      if (t === "format") {
        const fmt = this.configProxy.format || "";
        const s = String(src).replace(/\D/g, "");
        if (fmt === "mask:cpf_cnpj") {
          if (s.length === 11) return s.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
          if (s.length === 14) return s.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
        }
        if (fmt === "mask:cep" && s.length === 8) return s.replace(/(\d{5})(\d{3})/, "$1-$2");
        if (fmt === "uppercase") return String(src).toUpperCase();
        if (fmt === "lowercase") return String(src).toLowerCase();
        if (fmt === "trim") return String(src).trim();
        return src;
      }
      if (t === "map") {
        const mapObj = {};
        (this.mapRows || []).forEach(r => { if (r.from) mapObj[r.from] = r.to; });
        return mapObj[src] != null ? mapObj[src] : null;
      }
      if (t === "conditional") {
        const op = this.configProxy.operator || "equals";
        const pass = this.evalOperator(op, src, this.configProxy.value);
        return pass ? (this.configProxy.then || null) : (this.configProxy.else || null);
      }
      return src;
    },
  },
  methods: {
    emptyForm() {
      return {
        transformer_key: "",
        direction: "inbound",
        verso_field: "",
        source_expression: "",
        transform_type: "direct",
        transform_config: null,
        default_value: null,
        include_if_null: false,
        is_active: true,
        sort_order: 0,
        notes: "",
      };
    },
    setTransformType(key) {
      this.form.transform_type = key;
      this.configProxy = {};
      this.mapRows = [];
      this.sourcesList = [];
      if (key === "first_match") {
        this.configProxy = { set_mode: "pattern", pattern: "Properties{n}", range_min: 1, range_max: 64, operator: "equals", return_mode: "suffix" };
        this.firstMatchFields = [];
      }
    },
    getTransformerName(key) {
      const t = this.transformers.find(t => t.transformer_key === key);
      return t ? t.display_name : key;
    },
    open(overrideOrInitial) {
      this.treeSearch = "";
      this.saving = false;

      if (overrideOrInitial && overrideOrInitial.id) {
        this.isEditing = true;
        this.editId = overrideOrInitial.id;
        this.form = { ...this.emptyForm(), ...overrideOrInitial };
        this.loadConfigFromForm();
        if (this.form.transformer_key) this.loadPreview();
      } else {
        this.isEditing = false;
        this.editId = null;
        this.form = { ...this.emptyForm(), ...(overrideOrInitial || {}) };
        this.configProxy = {};
        this.mapRows = [];
        this.sourcesList = [];
        this.paramsList = [];
        this.firstMatchFields = [];
        if (this.form.transformer_key) this.loadPreview();
      }

      this.$modal.show("mappingRuleModal");
    },
    close() { this.$modal.hide("mappingRuleModal"); },
    loadConfigFromForm() {
      const c = this.form.transform_config || {};
      this.configProxy = { ...c };
      this.mapRows = c.map ? Object.entries(c.map).map(([from, to]) => ({ from, to })) : [];
      this.sourcesList = c.sources ? [...c.sources] : [];
      this.paramsList = c.params ? [...c.params] : [];
      this.firstMatchFields = [];

      // Hidrata config do first_match a partir de transform_config.field_set
      if (this.form.transform_type === "first_match") {
        const fs = c.field_set || {};
        this.configProxy = {
          set_mode: fs.mode === "list" ? "list" : "pattern",
          pattern: fs.pattern || "Properties{n}",
          range_min: fs.range_min != null ? fs.range_min : 1,
          range_max: fs.range_max != null ? fs.range_max : 64,
          operator: c.operator || "equals",
          value: c.value != null ? c.value : "",
          return_mode: c.return_mode || "suffix",
        };
        this.firstMatchFields = Array.isArray(fs.fields) ? [...fs.fields] : [];
      }
    },
    onTransformerChange() {
      this.previewData = {};
      if (this.form.transformer_key) this.loadPreview();
    },
    loadPreview() {
      this.loadingPreview = true;
      get(`/admin/erp-transformer-preview/${this.form.transformer_key}`)
        .then(res => { this.previewData = res.raw_data || {}; })
        .catch(() => { this.previewData = {}; })
        .finally(() => { this.loadingPreview = false; });
    },
    onFieldSelect(path) { this.form.source_expression = path; },
    onFieldAdd(path) {
      if (path && !this.sourcesList.includes(path)) this.sourcesList.push(path);
    },
    // Avalia um operador (reuso entre conditional e first_match)
    evalOperator(op, value, cmp) {
      if (op === "equals") return String(value) === String(cmp || "");
      if (op === "not_equals") return String(value) !== String(cmp || "");
      if (op === "is_empty") return !value;
      if (op === "is_not_empty") return !!value;
      if (op === "contains") return String(value).includes(cmp || "");
      if (op === "gt") return parseFloat(value) > parseFloat(cmp);
      if (op === "lt") return parseFloat(value) < parseFloat(cmp);
      return false;
    },
    // Expande o conjunto de campos do first_match (lista manual ou pattern)
    firstMatchFieldList() {
      if (this.configProxy.set_mode === "list") {
        return this.firstMatchFields.filter(Boolean);
      }
      const pattern = this.configProxy.pattern || "Properties{n}";
      const min = Number(this.configProxy.range_min) || 1;
      const max = Number(this.configProxy.range_max) || 0;
      const out = [];
      for (let n = min; n <= max; n++) out.push(pattern.replace(/\{n\}/g, n));
      return out;
    },
    buildConfig() {
      const t = this.form.transform_type;
      if (t === "direct") return null;
      if (t === "fixed") return { value: this.configProxy.value };
      if (t === "map") {
        const map = {};
        this.mapRows.forEach(r => { if (r.from) map[r.from] = r.to; });
        return { source: this.form.source_expression, map };
      }
      if (t === "conditional") return {
        source: this.form.source_expression,
        operator: this.configProxy.operator,
        value: this.configProxy.value,
        then: this.configProxy.then,
        else: this.configProxy.else,
      };
      if (t === "first_match") {
        const cp = this.configProxy;
        const isEmptyOp = cp.operator === "is_empty" || cp.operator === "is_not_empty";
        return {
          field_set: cp.set_mode === "list"
            ? { mode: "list", fields: this.firstMatchFields.filter(Boolean) }
            : { mode: "pattern", pattern: cp.pattern, range_min: Number(cp.range_min), range_max: Number(cp.range_max) },
          operator: cp.operator,
          value: isEmptyOp ? null : cp.value,
          return_mode: cp.return_mode || "suffix",
          default: null,
        };
      }
      if (t === "fallback" || t === "coalesce") return { sources: this.sourcesList.filter(Boolean) };
      if (t === "concat") return {
        sources: this.sourcesList.filter(Boolean),
        separator: this.configProxy.separator || " ",
        skip_empty: true,
      };
      if (t === "format") return { source: this.form.source_expression, format: this.configProxy.format };
      if (t === "system_var") return { var: this.configProxy.var };
      if (t === "custom") return { handler: this.configProxy.handler, params: this.paramsList.filter(Boolean) };
      return null;
    },
    save() {
      if (!this.canSave || this.saving) return;
      this.saving = true;
      const payload = {
        ...this.form,
        transform_config: this.buildConfig(),
        default_value: this.form.default_value || null,
      };
      const req = this.isEditing
        ? put(`/admin/erp-field-overrides/${this.editId}`, payload)
        : post("/admin/erp-field-overrides", payload);
      req
        .then(() => { this.$emit("saved"); this.close(); })
        .catch(err => {
          this.$swal && this.$swal({ title: "Erro ao salvar", text: err.message || "Tente novamente", icon: "error" });
        })
        .finally(() => { this.saving = false; });
    },
  },
};
</script>

<style scoped>
.cd-type-tabs { display: flex; flex-wrap: wrap; gap: 4px; }
.cd-modal-body { min-height: 380px; max-height: 60vh; overflow: hidden; }
.cd-tree-col { display: flex; flex-direction: column; overflow: hidden; max-height: 60vh; }
.cd-tree-scroll { flex: 1; overflow-y: auto; min-height: 0; }
.cd-form-col { overflow-y: auto; max-height: 60vh; }
.bg-success-light { background: #d4edda !important; }
.bg-info-light { background: #d1ecf1 !important; }
.cd-fallback-chain code { background: #fff; border: 1px solid #dee2e6; border-radius: 3px; padding: 1px 5px; font-size: 11.5px; }
.cd-source-item { background: #fff; border: 1px solid #dee2e6; border-radius: 4px; padding: 4px 8px; transition: box-shadow 0.1s; }
.cd-source-item:hover { border-color: #3a3e81; box-shadow: 0 1px 4px rgba(58,62,129,0.12); }
.cd-drag-ghost { opacity: 0.4; background: #eceaf3 !important; border: 1px dashed #3a3e81 !important; }
.cd-preview-bar { font-size: 12px; }
.font-monospace { font-family: 'SF Mono', Consolas, monospace !important; }
</style>
